import { useEffect, useState } from "react";
import { Loader2, Save, TestTube2, Mail, CheckCircle2, AlertTriangle, Server, Plug } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import api from "../../lib/api";

const inp = "bg-black/30 border-white/15 text-white mt-1";
const sel = "w-full bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm mt-1";

const EMPTY = {
  provider_name: "", smtp_host: "", smtp_port: 587, encryption: "tls",
  smtp_username: "", smtp_password: "", sender_name: "", sender_email: "",
  reply_to: "", reservation_notification_email: "", enabled: false,
};

const STATUS_LABEL = { active: "Active — sending live", disabled: "Disabled", not_configured: "Not fully configured" };

export default function AdminEmailSettings() {
  const [form, setForm] = useState(EMPTY);
  const [hasPassword, setHasPassword] = useState(false);
  const [status, setStatus] = useState(null);
  const [testRecipient, setTestRecipient] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConn, setTestingConn] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-settings");
      const s = data.settings || {};
      setForm({
        provider_name: s.provider_name || "", smtp_host: s.smtp_host || "",
        smtp_port: s.smtp_port || 587, encryption: s.encryption || "tls",
        smtp_username: s.smtp_username || "", smtp_password: "",
        sender_name: s.sender_name || "", sender_email: s.sender_email || "",
        reply_to: s.reply_to || "", reservation_notification_email: s.reservation_notification_email || "",
        enabled: !!s.enabled,
      });
      setHasPassword(!!s.has_password);
      setStatus(data.status || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load email configuration");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const payload = () => {
    const p = { ...form, smtp_port: Number(form.smtp_port) || 587 };
    if (!p.smtp_password) delete p.smtp_password; // blank keeps existing
    return p;
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/email-settings", payload());
      setStatus(data.status || null);
      setHasPassword(!!data.settings?.has_password);
      setForm((f) => ({ ...f, smtp_password: "" }));
      toast.success("Configuration saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save configuration"); }
    finally { setSaving(false); }
  };

  const testConfig = async () => {
    setTestingConn(true);
    try {
      const { data } = await api.post("/admin/email-settings/test-connection", payload());
      if (data.ok) toast.success("Connection successful — SMTP login verified");
      else toast.error(`Connection failed: ${data.error || "unknown error"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Connection test failed"); }
    finally { setTestingConn(false); }
  };

  const sendTest = async () => {
    if (!testRecipient.trim()) { toast.error("Enter a recipient email"); return; }
    setSendingTest(true);
    try {
      const { data } = await api.post("/admin/email-settings/test", { test_email: testRecipient });
      if (data.ok) toast.success(data.message || "Email Sent Successfully");
      else toast.error(`Could not send: ${data.message || "SMTP error"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send test email"); }
    finally { setSendingTest(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-24 text-white/60" data-testid="email-settings-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const state = status?.current_status;
  const dt = (o) => o?.at ? `${(o.at || "").slice(0, 16).replace("T", " ")}${o.to ? ` → ${o.to}` : ""}` : "—";

  return (
    <div className="space-y-6" data-testid="admin-email-settings">
      <div>
        <h1 className="font-cinzel text-2xl sm:text-3xl text-white tracking-wide">Email Configuration</h1>
        <p className="mt-1 text-muted-foreground text-sm">Generic SMTP — works with Zoho, Microsoft 365, Gmail Workspace, Amazon SES, ZeptoMail, SendGrid and any standard provider. The SMTP password is encrypted and never displayed.</p>
      </div>

      {/* Status */}
      <GlassCard className="p-5" hover={false} data-testid="email-service-status">
        <div className="flex items-center gap-3">
          <Server className="h-5 w-5 text-gold" />
          <div className="flex-1">
            <div className="text-white font-medium">{status?.provider_name || "SMTP"} · {status?.smtp_host || "—"}:{status?.smtp_port}</div>
            <div className="text-sm mt-0.5">
              {state === "active"
                ? <span className="inline-flex items-center gap-1.5 text-emerald-400"><CheckCircle2 className="h-4 w-4" />{STATUS_LABEL[state]}</span>
                : <span className="inline-flex items-center gap-1.5 text-amber"><AlertTriangle className="h-4 w-4" />{STATUS_LABEL[state] || state}</span>}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/10 text-sm">
          <div><div className="text-white/40 text-xs">Encryption</div><div className="text-white/80 uppercase">{status?.encryption}</div></div>
          <div><div className="text-white/40 text-xs">Sender</div><div className="text-white/80 truncate">{status?.sender_email}</div></div>
          <div><div className="text-white/40 text-xs">Enabled</div><div className="text-white/80">{status?.enabled ? "Yes" : "No"}</div></div>
          <div><div className="text-white/40 text-xs">Last Successful Test</div><div className="text-emerald-400/90">{dt(status?.last_success)}</div></div>
          <div><div className="text-white/40 text-xs">Last Failed Test</div><div className="text-firered/90">{dt(status?.last_failure)}</div></div>
        </div>
      </GlassCard>

      {/* SMTP settings */}
      <GlassCard className="p-6 space-y-4" hover={false}>
        <div className="flex items-center justify-between">
          <h2 className="text-white font-medium">SMTP Settings</h2>
          <label className="flex items-center gap-2 text-sm text-white/80"><Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="email-enabled-toggle" /> Email Enabled</label>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-white/80">Email Provider Name</Label><Input value={form.provider_name} onChange={(e) => set("provider_name", e.target.value)} placeholder="Zoho Mail" className={inp} data-testid="email-provider-name" /></div>
          <div><Label className="text-white/80">SMTP Host</Label><Input value={form.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} placeholder="smtppro.zoho.eu" className={inp} data-testid="email-smtp-host" /></div>
          <div><Label className="text-white/80">SMTP Port</Label><Input type="number" value={form.smtp_port} onChange={(e) => set("smtp_port", e.target.value)} placeholder="587" className={inp} data-testid="email-smtp-port" /></div>
          <div><Label className="text-white/80">Encryption</Label>
            <select value={form.encryption} onChange={(e) => set("encryption", e.target.value)} className={sel} data-testid="email-encryption">
              <option value="tls">TLS (STARTTLS · usually port 587)</option>
              <option value="ssl">SSL (implicit · usually port 465)</option>
            </select>
          </div>
          <div><Label className="text-white/80">SMTP Username</Label><Input value={form.smtp_username} onChange={(e) => set("smtp_username", e.target.value)} placeholder="sales@clactonfireworks.co.uk" className={inp} data-testid="email-smtp-username" /></div>
          <div><Label className="text-white/80">SMTP Password</Label><Input type="password" value={form.smtp_password} onChange={(e) => set("smtp_password", e.target.value)} placeholder={hasPassword ? "••••••••  (leave blank to keep current)" : "Enter SMTP password"} className={inp} data-testid="email-smtp-password" autoComplete="new-password" /></div>
          <div><Label className="text-white/80">Sender Name</Label><Input value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} placeholder="Clacton Fireworks" className={inp} data-testid="email-sender-name" /></div>
          <div><Label className="text-white/80">Sender Email</Label><Input type="email" value={form.sender_email} onChange={(e) => set("sender_email", e.target.value)} placeholder="sales@clactonfireworks.co.uk" className={inp} data-testid="email-sender-email" /></div>
          <div><Label className="text-white/80">Reply-To Email</Label><Input type="email" value={form.reply_to} onChange={(e) => set("reply_to", e.target.value)} placeholder="sales@clactonfireworks.co.uk" className={inp} data-testid="email-reply-to" /></div>
          <div><Label className="text-white/80">Reservation Notification Email</Label><Input type="email" value={form.reservation_notification_email} onChange={(e) => set("reservation_notification_email", e.target.value)} placeholder="sales@clactonfireworks.co.uk" className={inp} data-testid="email-notification" /></div>
        </div>
        <div className="flex flex-wrap justify-end gap-3 pt-3 border-t border-white/10">
          <Button variant="outline" className="border-white/20 text-white gap-2" onClick={testConfig} disabled={testingConn} data-testid="email-test-config-btn">
            {testingConn ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}Test Configuration
          </Button>
          <Button className="cf-gold-btn gap-2" onClick={save} disabled={saving} data-testid="email-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save Configuration
          </Button>
        </div>
      </GlassCard>

      {/* Test email */}
      <GlassCard className="p-6 space-y-4" hover={false} data-testid="email-test-card">
        <h2 className="text-white font-medium flex items-center gap-2"><Mail className="h-4 w-4 text-gold" />Send Test Email</h2>
        <p className="text-sm text-white/50">Sends a real email using the saved configuration. Save first so the test uses your latest settings.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-white/80">Recipient</Label>
            <Input type="email" value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)} placeholder="you@example.com" className={inp} data-testid="email-test-recipient" />
          </div>
          <Button variant="outline" className="border-white/20 text-white gap-2" onClick={sendTest} disabled={sendingTest} data-testid="email-send-test-btn">
            {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <TestTube2 className="h-4 w-4" />}Send Test Email
          </Button>
        </div>
      </GlassCard>
    </div>
  );
}
